import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Gem,
  Store,
  Coffee,
  Scissors,
  Dumbbell,
  Stethoscope,
  Home as HomeIcon,
  Briefcase,
  PartyPopper,
  PhoneCall,
  MessageCircle,
  CalendarCheck,
  Users,
  Footprints,
  Loader2,
  Wand2,
  Facebook,
  Rocket,
  Car,
  Wrench,
  Scale,
  Wallet,
  GraduationCap,
  Heart,
  Mic,
  HelpCircle,
  Plus,
  X as XIcon,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import GradientMesh from "@/components/GradientMesh";

const BUSINESS_TYPES = [
  { value: "Restaurant / Cafe", icon: Coffee },
  { value: "Salon / Beauty", icon: Scissors },
  { value: "Fitness / Wellness", icon: Dumbbell },
  { value: "Clinic / Health", icon: Stethoscope },
  { value: "Real Estate", icon: HomeIcon },
  { value: "Retail / Shop", icon: Store },
  { value: "Services / Consulting", icon: Briefcase },
  { value: "Events / Hospitality", icon: PartyPopper },
  { value: "Automotive", icon: Car },
  { value: "Contractor / Home Services", icon: Wrench },
  { value: "Legal / Professional Services", icon: Scale },
  { value: "Finance / Insurance", icon: Wallet },
  { value: "Education / Coaching", icon: GraduationCap },
  { value: "Nonprofit / Community", icon: Heart },
  { value: "Entertainment / Creator", icon: Mic },
  { value: "Other", icon: HelpCircle },
];

const TONES = [
  { value: "Friendly", description: "Warm, welcoming, approachable" },
  { value: "Professional", description: "Polished, trustworthy, expert" },
  { value: "Casual", description: "Easy, conversational, neighborly" },
  { value: "Bold", description: "Confident, punchy, direct" },
  { value: "Playful", description: "Fun, energetic, with light humor" },
  { value: "Luxurious", description: "Refined, elegant, premium" },
  { value: "Local / Community", description: "Talks to neighbors, references your city + regulars" },
  { value: "Promotional", description: "Clear reason to act now, still human" },
];

const GOALS = [
  { value: "walk_ins", label: "Foot traffic", icon: Footprints },
  { value: "calls", label: "Phone calls", icon: PhoneCall },
  { value: "dms", label: "DMs / inquiries", icon: MessageCircle },
  { value: "bookings", label: "Bookings", icon: CalendarCheck },
  { value: "brand_awareness", label: "Brand awareness", icon: Users },
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIME_PRESETS = ["09:00", "12:00", "15:00", "18:00", "20:00"];

// 24h "14:30" -> "2:30 PM" — used everywhere for display.
function to12h(hhmm) {
  if (!hhmm) return "";
  const [hStr, mStr] = String(hhmm).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || "0", 10);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function getTimezoneLabel() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
  } catch {
    return "Local time";
  }
}

const STEPS = [
  { key: "business", label: "Your business" },
  { key: "voice", label: "Brand voice" },
  { key: "goals", label: "What success looks like" },
  { key: "schedule", label: "When to post" },
  { key: "connect", label: "Connect & launch" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const isAdmin = !!user?.is_admin || !!user?.is_admin_eligible;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [fbStatus, setFbStatus] = useState(null);

  const defaultBusinessName = useMemo(() => {
    if (!user?.name) return "";
    return user.name;
  }, [user]);

  const [form, setForm] = useState({
    business_name: "",
    business_type: "",
    business_type_custom: "",
    location: "",
    tone: "Friendly",
    goals: [],
    posting_days: ["Mon", "Wed", "Fri"],
    posting_times: ["10:00"],
    // --- Phase NEW-1: optional business contact / operational details ---
    phone: "",
    business_email: "",
    website_url: "",
    booking_url: "",
    address: "",
    hours_of_operation: "",
    services_offered: "",
    target_customers: "",
    short_description: "",
    preferred_cta: "",
  });
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  // Pre-fill from current profile if available
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, ap, fb] = await Promise.all([
          api.get("/profile").catch(() => null),
          api.get("/autopilot/settings").catch(() => null),
          api.get("/connections/facebook/setup-status").catch(() => null),
        ]);
        if (cancelled) return;
        const profile = p?.data || {};
        const apc = ap?.data || {};
        setFbStatus(fb?.data || null);
        // If user already finished onboarding, redirect to dashboard
        if (profile?.onboarding_completed) {
          navigate("/dashboard", { replace: true });
          return;
        }
        setForm((s) => ({
          ...s,
          business_name: profile.business_name || defaultBusinessName,
          business_type: profile.business_type || "",
          business_type_custom: profile.business_type_custom || "",
          location: profile.location || "",
          tone: profile.tone || "Friendly",
          goals: Array.isArray(profile.goals) && profile.goals.length ? profile.goals : [],
          posting_days: Array.isArray(apc.posting_days) && apc.posting_days.length
            ? apc.posting_days
            : ["Mon", "Wed", "Fri"],
          posting_times: Array.isArray(apc.posting_times) && apc.posting_times.length
            ? apc.posting_times
            : ["10:00"],
          phone: profile.phone || "",
          business_email: profile.business_email || "",
          website_url: profile.website_url || "",
          booking_url: profile.booking_url || "",
          address: profile.address || "",
          hours_of_operation: profile.hours_of_operation || "",
          services_offered: profile.services_offered || "",
          target_customers: profile.target_customers || "",
          short_description: profile.short_description || "",
          preferred_cta: profile.preferred_cta || "",
        }));
      } catch (_e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [defaultBusinessName, navigate]);

  const canContinue = (() => {
    if (step === 0) {
      if (!form.business_name || !form.business_type) return false;
      if (form.business_type === "Other" && !(form.business_type_custom || "").trim()) return false;
      return true;
    }
    if (step === 1) return !!form.tone;
    if (step === 2) return true; // goals optional
    if (step === 3) return form.posting_days.length > 0 && form.posting_times.length > 0;
    return true;
  })();

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleArr = (key, value) =>
    setForm((s) => {
      const arr = s[key] || [];
      return {
        ...s,
        [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        business_name: form.business_name,
        business_type: form.business_type,
        business_type_custom: form.business_type_custom,
        location: form.location,
        tone: form.tone,
        goals: form.goals,
        // Phase NEW-1 optional details — only sent if user filled anything in
        phone: form.phone || "",
        business_email: form.business_email || "",
        website_url: form.website_url || "",
        booking_url: form.booking_url || "",
        address: form.address || "",
        hours_of_operation: form.hours_of_operation || "",
        services_offered: form.services_offered || "",
        target_customers: form.target_customers || "",
        short_description: form.short_description || "",
        preferred_cta: form.preferred_cta || "",
      });
      await api.put("/autopilot/settings", {
        mode: "autopilot",
        posting_days: form.posting_days,
        posting_times: form.posting_times,
        target_audience:
          form.location ? `local customers near ${form.location}` : "local customers",
      });
    } catch (e) {
      toast.error("Could not save your details");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const completeAndGo = async (target = "/dashboard") => {
    try {
      await api.put("/profile", { onboarding_completed: true });
      await refresh?.();
      navigate(target, { replace: true });
    } catch (_e) {
      navigate(target, { replace: true });
    }
  };

  const connectDemoAndLaunch = async () => {
    setLaunching(true);
    try {
      await saveProfile();
      // If user doesn't already have a connection, create a demo one
      if (!fbStatus?.connected) {
        await api.post("/connections/facebook/demo-connect", {
          page_name: `${form.business_name} · Demo`,
        });
      }
      toast.success("You're all set! Your AI marketing assistant is ready.");
      await completeAndGo("/dashboard");
    } catch (_e) {
      // saveProfile already shows toast
    } finally {
      setLaunching(false);
    }
  };

  const skipForNow = async () => {
    setLaunching(true);
    try {
      await saveProfile();
      toast.success("Saved. You can finish setup later from the sidebar.");
      await completeAndGo("/dashboard");
    } finally {
      setLaunching(false);
    }
  };

  const totalSteps = STEPS.length;
  const pct = Math.round(((step + 1) / totalSteps) * 100);

  return (
    <div className="relative min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] flex items-stretch" data-testid="onboarding-page">
      <GradientMesh />
      {/* Left rail: branding + step list */}
      <aside className="hidden md:flex flex-col w-[300px] shrink-0 border-r border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]/60 backdrop-blur p-6 relative">
        <div className="flex items-center gap-2 mb-8">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] shadow-[0_4px_18px_rgba(27,168,159,0.45)]">
            <Gem className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold leading-none">Postmoro</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)] mt-1">welcome</div>
          </div>
        </div>
        <div className="text-sm text-[color:var(--text-2)] leading-relaxed mb-6">
          Let's set up your <span className="text-[color:var(--text-1)] font-semibold">AI marketing assistant</span>. Takes about 2 minutes.
        </div>
        <ol className="space-y-2 mt-2" data-testid="onboarding-steps">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <li
                key={s.key}
                className={
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition " +
                  (active
                    ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)] shadow-[inset_0_0_0_1px_rgba(27,168,159,0.35)]"
                    : done
                    ? "text-[color:var(--text-3)]"
                    : "text-[color:var(--text-3)]")
                }
              >
                <div className={
                  "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold " +
                  (done
                    ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)]"
                    : active
                    ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)]"
                    : "bg-[color:var(--surface-3)] text-[color:var(--text-2)]")
                }>
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </div>
                <span>{s.label}</span>
              </li>
            );
          })}
        </ol>
      </aside>

      {/* Main */}
      <main className="flex-1 relative flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          {/* Progress (mobile + tablet) */}
          <div className="md:hidden mb-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">
              Step {step + 1} of {totalSteps}
            </div>
            <div className="h-1.5 mt-2 bg-[color:var(--surface-3)] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] transition-all" style={{ width: pct + "%" }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              {step === 0 && (
                <section data-testid="onboarding-step-business">
                  <h1 className="font-display text-3xl font-semibold tracking-tight">Tell me about your business</h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-2">
                    I'll use this to write captions that sound like you and reach your local customers.
                  </p>
                  <div className="mt-6 grid grid-cols-1 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[color:var(--text-3)]">Business name</label>
                      <Input
                        value={form.business_name}
                        onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))}
                        placeholder="e.g. Bella Pizza"
                        data-testid="onboarding-business-name"
                        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[color:var(--text-3)]">What kind of business?</label>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="onboarding-business-type-grid">
                        {BUSINESS_TYPES.map((t) => {
                          const Icon = t.icon;
                          const active = form.business_type === t.value;
                          return (
                            <button
                              key={t.value}
                              type="button"
                              onClick={() => setForm((s) => ({ ...s, business_type: t.value }))}
                              data-testid={`onboarding-business-type-${t.value.split(" ")[0].toLowerCase()}`}
                              className={
                                "rounded-md border px-3 py-3 text-xs text-left transition " +
                                (active
                                  ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                                  : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:bg-[color:var(--surface-3)]")
                              }
                            >
                              <Icon className={"h-4 w-4 mb-1.5 " + (active ? "text-[color:var(--brand-primary)]" : "")} />
                              <div>{t.value}</div>
                            </button>
                          );
                        })}
                      </div>
                      {form.business_type === "Other" ? (
                        <div className="mt-3" data-testid="onboarding-business-type-custom-wrap">
                          <label className="text-xs uppercase tracking-wider text-[color:var(--text-3)]">
                            Describe your business type
                          </label>
                          <Input
                            value={form.business_type_custom}
                            onChange={(e) => setForm((s) => ({ ...s, business_type_custom: e.target.value }))}
                            placeholder="e.g. Mobile dog grooming, drone photography studio, art gallery..."
                            data-testid="onboarding-business-type-custom"
                            className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                            maxLength={80}
                          />
                          <div className="text-[11px] text-[color:var(--text-3)] mt-1.5">
                            We'll use this in your AI captions, brand voice, and marketing ideas.
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[color:var(--text-3)]">Location <span className="text-[color:var(--text-3)] normal-case">(optional)</span></label>
                      <Input
                        value={form.location}
                        onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                        placeholder="e.g. Brooklyn, NY"
                        data-testid="onboarding-location"
                        className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                      />
                    </div>

                    {/* Phase NEW-1: Optional business contact / operational details */}
                    <div className="mt-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]/50 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowOptionalDetails((v) => !v)}
                        data-testid="onboarding-optional-details-toggle"
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[color:var(--surface-3)] transition"
                        aria-expanded={showOptionalDetails}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-[color:var(--text-1)]">
                            Add business details for better posts (optional)
                          </span>
                          <span className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                            The more details you add, the more accurate your posts will be. You can skip anything and update it later.
                          </span>
                        </div>
                        <span className={"text-[color:var(--text-3)] transition-transform " + (showOptionalDetails ? "rotate-180" : "")}>▾</span>
                      </button>

                      {showOptionalDetails ? (
                        <div className="px-4 pb-4 pt-1 border-t border-[color:var(--border-subtle)] grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-optional-details-fields">
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Short description</label>
                            <Input
                              value={form.short_description}
                              onChange={(e) => setForm((s) => ({ ...s, short_description: e.target.value }))}
                              placeholder="e.g. Neighborhood pizzeria serving wood-fired pies since 2014"
                              data-testid="onboarding-short-description"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={300}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Phone</label>
                            <Input
                              value={form.phone}
                              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                              placeholder="(555) 123-4567"
                              data-testid="onboarding-phone"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={40}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Business email</label>
                            <Input
                              type="email"
                              value={form.business_email}
                              onChange={(e) => setForm((s) => ({ ...s, business_email: e.target.value }))}
                              placeholder="hello@yourbusiness.com"
                              data-testid="onboarding-business-email"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={120}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Website URL</label>
                            <Input
                              value={form.website_url}
                              onChange={(e) => setForm((s) => ({ ...s, website_url: e.target.value }))}
                              placeholder="https://yourbusiness.com"
                              data-testid="onboarding-website-url"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={300}
                            />
                          </div>
                          <div>
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Booking / appointment link</label>
                            <Input
                              value={form.booking_url}
                              onChange={(e) => setForm((s) => ({ ...s, booking_url: e.target.value }))}
                              placeholder="https://calendly.com/yourbusiness"
                              data-testid="onboarding-booking-url"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={300}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Address</label>
                            <Input
                              value={form.address}
                              onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))}
                              placeholder="123 Main St, Brooklyn, NY 11201"
                              data-testid="onboarding-address"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={300}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Hours of operation</label>
                            <Input
                              value={form.hours_of_operation}
                              onChange={(e) => setForm((s) => ({ ...s, hours_of_operation: e.target.value }))}
                              placeholder="Mon–Fri 8a–8p · Sat–Sun 9a–9p"
                              data-testid="onboarding-hours"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={500}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Services / products offered</label>
                            <Input
                              value={form.services_offered}
                              onChange={(e) => setForm((s) => ({ ...s, services_offered: e.target.value }))}
                              placeholder="e.g. Wood-fired pizza, fresh pasta, weekly specials, catering"
                              data-testid="onboarding-services"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={800}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Target customers</label>
                            <Input
                              value={form.target_customers}
                              onChange={(e) => setForm((s) => ({ ...s, target_customers: e.target.value }))}
                              placeholder="e.g. Families, students, professionals in the neighborhood"
                              data-testid="onboarding-target-customers"
                              className="mt-1.5 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                              maxLength={400}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Preferred call-to-action</label>
                            <select
                              value={form.preferred_cta}
                              onChange={(e) => setForm((s) => ({ ...s, preferred_cta: e.target.value }))}
                              data-testid="onboarding-preferred-cta"
                              className="mt-1.5 h-10 w-full rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] text-sm text-[color:var(--text-1)] px-3"
                            >
                              <option value="">No preference — let AI decide</option>
                              <option value="Call now">Call now</option>
                              <option value="Book online">Book online</option>
                              <option value="Visit our website">Visit our website</option>
                              <option value="Stop in today">Stop in today</option>
                              <option value="Message us">Message us</option>
                              <option value="Get a quote">Get a quote</option>
                              <option value="Schedule appointment">Schedule appointment</option>
                            </select>
                          </div>
                          <div className="sm:col-span-2 text-[11px] text-[color:var(--text-3)] mt-1">
                            Postmoro will only use these details to personalize your content. You can edit or remove them anytime from Settings → Business Profile.
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              )}

              {step === 1 && (
                <section data-testid="onboarding-step-voice">
                  <h1 className="font-display text-3xl font-semibold tracking-tight">How should I sound?</h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-2">
                    Pick the voice that matches how you'd talk to your customers in person.
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-tone-grid">
                    {TONES.map((t) => {
                      const active = form.tone === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setForm((s) => ({ ...s, tone: t.value }))}
                          data-testid={`onboarding-tone-${t.value.toLowerCase()}`}
                          className={
                            "text-left rounded-md border p-3 transition " +
                            (active
                              ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.08)]"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)]")
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-[color:var(--text-1)]">{t.value}</div>
                            {active ? <Check className="h-4 w-4 text-[color:var(--brand-primary)]" /> : null}
                          </div>
                          <div className="text-xs text-[color:var(--text-2)] mt-1">{t.description}</div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {step === 2 && (
                <section data-testid="onboarding-step-goals">
                  <h1 className="font-display text-3xl font-semibold tracking-tight">What does success look like?</h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-2">
                    Pick a few. I'll bias your content toward driving these results.
                  </p>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="onboarding-goals-grid">
                    {GOALS.map((g) => {
                      const Icon = g.icon;
                      const active = form.goals.includes(g.value);
                      return (
                        <button
                          key={g.value}
                          type="button"
                          onClick={() => toggleArr("goals", g.value)}
                          data-testid={`onboarding-goal-${g.value}`}
                          className={
                            "rounded-md border p-3 text-sm transition flex flex-col items-start gap-2 " +
                            (active
                              ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.08)]"
                              : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:bg-[color:var(--surface-3)]")
                          }
                        >
                          <Icon className={"h-5 w-5 " + (active ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-3)]")} />
                          <div className="font-medium text-[color:var(--text-1)]">{g.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {step === 3 && (
                <section data-testid="onboarding-step-schedule">
                  <h1 className="font-display text-3xl font-semibold tracking-tight">When should I post?</h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-2">
                    Pick your posting days and times. I'll spread content evenly across the week.
                  </p>
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-wider text-[color:var(--text-3)]">Posting days</div>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="onboarding-days-grid">
                      {WEEKDAYS.map((d) => {
                        const active = form.posting_days.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => toggleArr("posting_days", d)}
                            data-testid={`onboarding-day-${d.toLowerCase()}`}
                            className={
                              "h-10 w-12 rounded-md border text-sm font-medium transition " +
                              (active
                                ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.12)] text-[color:var(--text-1)]"
                                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:bg-[color:var(--surface-3)]")
                            }
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-wider text-[color:var(--text-3)] flex items-center justify-between gap-2">
                      <span>Posting times</span>
                      <span className="inline-flex items-center gap-1 text-[10px] normal-case text-[color:var(--text-3)]">
                        <Globe className="h-3 w-3" /> {getTimezoneLabel()}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="onboarding-times-grid">
                      {TIME_PRESETS.map((t) => {
                        const active = form.posting_times.includes(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => toggleArr("posting_times", t)}
                            data-testid={`onboarding-time-${t.replace(":", "")}`}
                            className={
                              "h-10 px-3 rounded-md border text-sm font-medium transition inline-flex items-center gap-1.5 " +
                              (active
                                ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.12)] text-[color:var(--text-1)]"
                                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:bg-[color:var(--surface-3)]")
                            }
                          >
                            {to12h(t)}
                            {active ? <Check className="h-3 w-3 text-[color:var(--brand-primary)]" /> : null}
                          </button>
                        );
                      })}
                    </div>
                    {/* Custom times (chip list with remove) */}
                    {form.posting_times.filter((t) => !TIME_PRESETS.includes(t)).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="onboarding-custom-times-grid">
                        {form.posting_times.filter((t) => !TIME_PRESETS.includes(t)).map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.12)] text-sm font-medium text-[color:var(--text-1)]"
                            data-testid={`onboarding-custom-time-${t.replace(":", "")}`}
                          >
                            {to12h(t)}
                            <button
                              type="button"
                              onClick={() => setForm((s) => ({ ...s, posting_times: s.posting_times.filter((x) => x !== t) }))}
                              className="text-[color:var(--text-3)] hover:text-[color:var(--danger)]"
                              aria-label={`Remove ${to12h(t)}`}
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {/* Add custom time row */}
                    <div className="mt-3 flex items-center gap-2" data-testid="onboarding-add-custom-time-row">
                      <Input
                        type="time"
                        value={form.custom_time_draft || ""}
                        onChange={(e) => setForm((s) => ({ ...s, custom_time_draft: e.target.value }))}
                        className="h-10 w-32 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                        data-testid="onboarding-custom-time-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
                        data-testid="onboarding-add-custom-time"
                        onClick={() => {
                          const v = (form.custom_time_draft || "").trim();
                          if (!v || form.posting_times.includes(v)) return;
                          setForm((s) => ({
                            ...s,
                            posting_times: [...s.posting_times, v].sort(),
                            custom_time_draft: "",
                          }));
                        }}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Add time
                      </Button>
                      <span className="text-[11px] text-[color:var(--text-3)]">
                        Add as many as you like — preview as 12-hour AM/PM.
                      </span>
                    </div>
                    <div className="text-[11px] text-[color:var(--text-3)] mt-3">
                      Tip: 2–3 days a week with 1 strong time is plenty for local businesses. You can edit these later in Autopilot.
                    </div>
                  </div>
                </section>
              )}

              {step === 4 && (
                <section data-testid="onboarding-step-connect">
                  <h1 className="font-display text-3xl font-semibold tracking-tight">Ready to launch?</h1>
                  <p className="text-sm text-[color:var(--text-2)] mt-2">
                    You can connect social accounts later from Connections — let's get you to your dashboard.
                  </p>
                  <div className="mt-6 grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      onClick={() => completeAndGo("/dashboard")}
                      disabled={launching}
                      data-testid="onboarding-continue-dashboard"
                      className="h-12 w-full bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold text-base"
                    >
                      {launching ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-2" />
                      )}
                      Continue to Dashboard
                    </Button>

                    <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)] mt-2">
                      Optional · do later from Connections
                    </div>

                    {/* Admin-only: launch in demo mode. Hidden from real customers
                        so they don't get confused by simulated publishing. */}
                    {isAdmin ? (
                    <button
                      type="button"
                      onClick={connectDemoAndLaunch}
                      disabled={launching}
                      data-testid="onboarding-launch-demo"
                      className="text-left rounded-xl border border-[color:var(--accent-warm)] bg-[color:var(--surface-2)] p-4 hover:border-[color:var(--accent-warm)] hover:bg-[color:rgba(255,138,61,0.04)] transition disabled:opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-[color:var(--accent-warm)] text-[color:var(--bg-0)] flex items-center justify-center">
                          {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                        </div>
                        <div className="font-semibold text-base text-[color:var(--text-1)]">Launch in Demo mode <span className="text-[10px] uppercase tracking-wider text-[color:var(--accent-warm)] ml-1">admin only</span></div>
                      </div>
                      <div className="text-sm text-[color:var(--text-2)] mt-2">
                        Spins up a simulated Page for internal QA. Posts never reach Facebook and are labeled Demo in Content Manager.
                      </div>
                    </button>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => completeAndGo("/settings/connections")}
                      disabled={launching}
                      data-testid="onboarding-launch-real"
                      className="text-left rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4 hover:border-[color:var(--text-3)] transition disabled:opacity-60"
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-[#1877F2]/15 text-[#1877F2] flex items-center justify-center">
                          <Facebook className="h-4 w-4" />
                        </div>
                        <div className="font-semibold text-base text-[color:var(--text-1)]">Connect a real Facebook Page</div>
                      </div>
                      <div className="text-sm text-[color:var(--text-2)] mt-2">
                        Takes ~10 minutes. Publish directly to Facebook Pages and Instagram professional accounts from Postmoro.
                      </div>
                    </button>
                  </div>
                </section>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Footer actions */}
          <div className="mt-8 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 0 || launching}
              data-testid="onboarding-back"
              className="text-[color:var(--text-2)]"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>

            {step < STEPS.length - 1 ? (
              <Button
                onClick={async () => {
                  if (step === 3) {
                    try {
                      await saveProfile();
                    } catch (_e) {
                      return;
                    }
                  }
                  next();
                }}
                disabled={!canContinue || saving}
                data-testid="onboarding-next"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold px-5"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Continue <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <div className="text-xs text-[color:var(--text-3)] inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-[color:var(--brand-primary)]" />
                We'll generate your first week as soon as you launch
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
